import { ArrowRight } from "lucide-react";

import { Badge, Table, type Column } from "../../components";
import { t } from "../../i18n/t";

/**
 * §B4.2's row. `source_name`, `target_name`, `renamed`, `reason` and `badges` are not columns on `coa_mappings`
 * (§0.3) and are assumed enriched by the API's response model; each is optional here and renders as
 * a blank when absent. The name is never invented client-side.
 */
export interface CoaRow {
  source_code: string;
  source_name?: string | null;
  account_type?: string | null;
  system_role?: string | null;
  /** The plain label for the System role column (server-side); blank when the role has none. */
  system_role_label?: string | null;
  target_code?: string | null;
  target_name?: string | null;
  status?: string | null;
  renamed?: boolean | null;
  reason?: string | null;
  badges?: string[] | null;
  /** xmg-coafast: `describe_landing`, the server's one sentence for where this account lands. */
  summary?: string | null;
}

/**
 * The badge vocabulary of §B4.2. The map holds the **translated string**, not the key: 06a's `tests/i18n.test.ts`
 * finds a reference by matching a quoted literal inside the translate call, so a key stored in a table and
 * resolved at the call site reads as unreferenced and fails the "(b) every key is referenced" assertion. Calling
 * the translator at module scope is safe — `en.ts` is a frozen object and 1d has no locale switch.
 */
const BADGE_LABELS: Readonly<Record<string, string>> = {
  SYSTEM_ACCOUNT: t("mapping.badge_system_account"),
  RENUMBERED: t("mapping.badge_renumbered"),
  RENAMED: t("mapping.badge_renamed"),
  GREENFIELD: t("mapping.badge_greenfield"),
  BLOCKED: t("mapping.badge_blocked"),
};

function badgeLabel(badge: string): string {
  return BADGE_LABELS[badge] ?? badge;
}

/** §B7.3: a badge is text plus colour, never colour alone. */
export function CoaTable({
  rows,
  targetKey = null,
}: {
  rows: readonly CoaRow[];
  /** xmg-webux: the row "Open the tab to choose" jumped to (highlighted, scrolled to and focused). */
  targetKey?: string | null;
}): JSX.Element {
  // xmg-review-layout: four columns instead of nine, so the source account and the account it lands in sit
  // side by side at laptop widths (the old nine-column strip pushed the destination off the right edge).
  // Each cell stacks its facts (code, name, secondary detail) and wraps; below the container breakpoint the
  // row becomes a card (source above destination). Test ids are unchanged.
  const columns: readonly Column<CoaRow>[] = [
    {
      key: "source",
      header: t("mapping.col_source_account"),
      cellClassName: "coa__source",
      render: (row) => (
        <span className="acct">
          <span className="acct__code mono">{row.source_code}</span>
          <span className="acct__name">{row.source_name ?? ""}</span>
          {row.account_type || row.system_role_label ? (
            <span className="acct__meta muted">
              {row.account_type ? <span>{row.account_type}</span> : null}
              {row.system_role_label ? <span>{row.system_role_label}</span> : null}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "arrow",
      header: t("mapping.col_maps_to"),
      hiddenHeader: true,
      cellClassName: "coa__arrow",
      render: () => <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />,
    },
    {
      key: "target",
      header: t("mapping.col_target_account"),
      cellClassName: "coa__target",
      render: (row) => (
        <span className="acct">
          <span className="acct__code mono" data-testid={`coa-target-${row.source_code}`}>
            {row.target_code ?? ""}
          </span>
          {row.target_code ? (
            <span className="acct__name">{row.target_name ?? ""}</span>
          ) : (
            <span className="acct__name coa__pending">{t("mapping.not_mapped_yet")}</span>
          )}
          <span className="acct__meta muted" data-testid={`coa-summary-${row.source_code}`}>
            {row.summary ?? ""}
          </span>
        </span>
      ),
    },
    {
      key: "status",
      header: t("mapping.col_status"),
      cellClassName: "coa__status",
      render: (row) => (
        <span className="coa__badges">
          <Badge tone={row.status === "MAPPED" ? "pass" : "fail"}>
            <span data-testid={`coa-status-${row.source_code}`}>{row.status ?? ""}</span>
          </Badge>
          {(row.badges ?? []).map((badge) => (
            <Badge key={badge} tone={badge === "BLOCKED" ? "fail" : "neutral"}>
              {badgeLabel(badge)}
            </Badge>
          ))}
          {(row.badges ?? []).includes("RENUMBERED") ? (
            <span className="muted coa__note" data-testid={`coa-renumbered-${row.source_code}`}>
              {t("mapping.renumbered_from", {
                from: row.source_code,
                to: row.target_code ?? "",
                reason: row.reason ?? "",
              })}
            </span>
          ) : null}
        </span>
      ),
    },
  ];

  // What still needs an answer comes first (stable otherwise). The backend emits MAPPED or BLOCKED; anything
  // other than MAPPED is treated as open, so a status this screen does not know by name is never hidden.
  const isOpen = (row: CoaRow): boolean => row.status !== "MAPPED";
  const open = rows.filter(isOpen);
  const ordered = [...open, ...rows.filter((row) => !isOpen(row))];
  const needAccount = open.length;

  return (
    <div className="coatable">
      {rows.length === 0 ? null : (
        <p className="tablesummary" data-testid="coa-counts">
          {t("mapping.coa_counts", { open: needAccount, mapped: rows.length - needAccount })}
        </p>
      )}
      <Table
        wrap
        caption={t("review.caption_accounts")}
        columns={columns}
        rows={ordered}
        rowKey={(row) => row.source_code}
        emptyMessage={t("review.empty")}
        // §3: an open (blocked) row takes the fail-row tint, in both themes (ReviewDark.html).
        rowClassName={(row) =>
          row.source_code === targetKey ? "is-target" : isOpen(row) ? "is-fail" : undefined
        }
        rowDataKey={(row) => row.source_code}
      />
    </div>
  );
}
